import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsOpen(false);
    window.open(url, '_blank', 'noopener');
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 bg-background-50"></div>
      <div className="absolute right-0 top-0 w-1/2 h-full hidden lg:block">
        <div className="absolute inset-0 bg-accent-100/30"></div>
        <img
          src="https://readdy.ai/api/search-image?query=Curated%20fashion%20editorial%20featuring%20layered%20neutral%20toned%20garments%20artfully%20arranged%20on%20a%20warm%20travertine%20surface%2C%20soft%20morning%20light%20from%20skylight%2C%20linen%20shirt%20beside%20leather%20accessories%20and%20folded%20cashmere%2C%20architectural%20shadows%2C%20italian%20boutique%20window%20display%20aesthetic%2C%20elevated%20everyday%20luxury%2C%20no%20models&width=1400&height=1800&seq=hero-fashion-v2&orientation=portrait"
          alt="Itaobuy Spreadsheet fashion products"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent"></div>
      </div>

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 md:px-6">
        <div className="w-full lg:w-1/2 pt-32 pb-16 md:pt-40 md:pb-24">
          <h1 className="hero-title text-4xl md:text-6xl lg:text-7xl text-foreground-950 leading-tight mb-6">
            {t('hero.title')}
          </h1>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-700 font-medium mb-4">
            {t('hero.subtitle')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-10 max-w-xl">
            {t('hero.description')}
          </p>

          <div className="relative mb-8" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full max-w-md flex items-center justify-between bg-background-50 border border-accent-500/40 rounded-2xl px-5 py-4 text-sm text-foreground-700 hover:border-accent-500 transition-colors cursor-pointer"
            >
              <span>{t('categories.select')}</span>
              <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500`}></i>
            </button>

            {isOpen && (
              <div className="absolute top-full mt-2 w-full max-w-md bg-background-50 border border-background-200/70 rounded-2xl shadow-sm overflow-hidden z-20 max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategorySelect(cat.url)}
                    className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-accent-100/50 hover:text-foreground-950 transition-colors flex items-center gap-3 cursor-pointer"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-500 flex-shrink-0"></span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="group inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md font-label text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('hero.cta_primary')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="group inline-flex items-center justify-center gap-2 border border-primary-500 text-primary-500 px-8 py-3.5 rounded-md font-label text-sm hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('hero.cta_secondary')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          <div className="flex flex-wrap gap-4 mt-10">
            {['hero.trust_1', 'hero.trust_2', 'hero.trust_3', 'hero.trust_4'].map((key) => (
              <span key={key} className="flex items-center gap-2 text-xs text-foreground-500">
                <i className="ri-check-line text-accent-500 text-sm"></i>
                {t(key)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}